import {BingIcon} from '@github-ui/copilot-reference-preview/components/WebSearchReferencePreview'
import {
  AgentIcon,
  BookIcon,
  CommentDiscussionIcon,
  FileIcon,
  PlayIcon,
  SearchIcon,
  ShieldIcon,
  TagIcon,
  TasklistIcon,
} from '@primer/octicons-react'
import {Heading} from '@primer/react'
import {Truncate} from '@primer/styled-react'
import {useMemo} from 'react'

import type {CopilotChatManager} from '../utils/copilot-chat-manager'
import {
  type CopilotChatReference,
  type FunctionArguments,
  type SkillExecution,
  SUPPORTED_FUNCTIONS,
} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {copilotLocalStorage} from '../utils/copilot-local-storage'
import {
  BingGroundingFunctionButton,
  CodeSearchButton,
  FigmaButton,
  GetAlertButton,
  GetDiscussionButton,
  GetFileButton,
  GetFileChangesButton,
  GetGitHubDataButton,
  GetJobLogsButton,
  getJobLogsSkillExecutionTopic,
  PlanButton,
  SupportSearchButton,
} from './FunctionCallButton'
import styles from './FunctionLoadingUtils.module.css'

export const CodeSearchIcon = () => <SearchIcon />
export const GetFileIcon = () => <FileIcon />
const ReleaseIcon = () => <TagIcon />
const JobIcon = () => <PlayIcon />
const SearchBookIcon = () => <BookIcon />
const DiscussionIcon = () => <CommentDiscussionIcon />
const PlanIcon = () => <TasklistIcon />
const IssueDraftIcon = () => <IssueDraftIcon />
const AgentTaskIcon = () => <AgentIcon />

const DEFAULT_RESPONDING_MESSAGE = 'planning response'

interface FunctionMetadata {
  id: string
  loadingText: string
  completedText: string
  icon: () => React.ReactElement
  referenceType?: CopilotChatReference['type']
  name: string
  activeSkillText: string
}

export const functionMetadataCollection = (args: FunctionArguments): FunctionMetadata | undefined => {
  switch (args.kind) {
    case 'bing-search':
      return {
        id: 'bing-search',
        referenceType: 'web-search',
        loadingText: `Using Bing to search for "${args.query}"`,
        completedText: `Bing results for "${args.query}"`,
        icon: BingIcon,
        name: 'Bing search',
        activeSkillText: 'searching Bing',
      }
    case 'lexical-code-search':
      return {
        id: 'codesearch',
        loadingText: `Search for "${args.query}" in "${args.scopingQuery}"`,
        completedText: `Search results for "${args.query}" in "${args.scopingQuery}"`,
        icon: CodeSearchIcon,
        name: 'Code search',
        activeSkillText: 'searching the codebase',
      }
    case 'semantic-code-search':
      return {
        id: 'codesearch',
        loadingText: `Search for "${args.query}" in "${args.repoOwner}/${args.repoName}"`,
        completedText: `Search results for "${args.query}" in "${args.repoOwner}/${args.repoName}"`,
        icon: CodeSearchIcon,
        name: 'Code search',
        activeSkillText: 'searching the codebase',
      }
    case 'getalert':
      return {
        id: 'getalert',
        loadingText: `Retrieving information about alerts`,
        completedText: `Including information about alerts`,
        icon: () => <ShieldIcon />,
        name: 'Get alert',
        activeSkillText: 'getting alert',
      }
    case 'get-github-data': {
      return {
        id: 'get-github-data',
        loadingText: `Fetching ${args.endpoint}`,
        completedText: `Results of ${args.endpoint}`,
        icon: ReleaseIcon,
        name: 'Get GitHub Data',
        activeSkillText: 'getting data',
      }
    }
    case 'get-actions-job-logs': {
      const topic = getJobLogsSkillExecutionTopic(args)
      const capitalizedTopic = topic.charAt(0).toUpperCase() + topic.slice(1)
      return {
        id: 'get-actions-jog-logs',
        loadingText: `Retrieving ${topic} in ${args.repo}`,
        completedText: `${capitalizedTopic} in ${args.repo}`,
        icon: JobIcon,
        name: 'Get job logs',
        activeSkillText: 'getting job logs',
      }
    }
    case 'getdiscussion': {
      return {
        id: 'getdiscussion',
        loadingText: `Fetching discussion ${args.discussionNumber}`,
        completedText: `Details about discussion #${args.discussionNumber}`,
        icon: DiscussionIcon,
        name: 'Get discussion',
        activeSkillText: 'getting discussion',
      }
    }
    case 'getfile': {
      return {
        id: 'getfile',
        loadingText: `Searching for file`,
        completedText: `Using results of get file`,
        icon: GetFileIcon,
        name: 'Get file',
        activeSkillText: 'searching for file',
      }
    }
    case 'getfilechanges': {
      return {
        id: 'getfilechanges',
        loadingText: `Searching for file changes`,
        completedText: `File changes for ${args.path} in ${args.repo}`,
        icon: GetFileIcon,
        name: 'Get file changes',
        activeSkillText: 'searching for file changes',
      }
    }
    case 'planskill': {
      return {
        id: 'planskill',
        loadingText: `Planning response...`,
        completedText: `Plan created`,
        icon: PlanIcon,
        name: 'Plan skill',
        activeSkillText: 'planning response',
      }
    }
    case 'support-search': {
      return {
        id: 'supportsearch',
        loadingText: `Searching for support documents`,
        completedText: `Using results of support search`,
        icon: SearchBookIcon,
        name: 'Support search',
        activeSkillText: 'searching for support documents',
      }
    }
    case 'get-figma': {
      return {
        id: 'getfigma',
        loadingText: 'Analyzing Figma file',
        completedText: 'Using results of Figma file',
        icon: SearchBookIcon,
        name: 'Get Figma',
        activeSkillText: 'analyzing Figma file',
      }
    }
    case 'codesearchagentskill': {
      return {
        id: 'codesearchagentskill',
        loadingText: 'Using the code search agent',
        completedText: 'Using results of code search agent',
        icon: SearchBookIcon,
        name: 'Code Search Agent',
        activeSkillText: 'searching the codebase',
      }
    }
    case 'github-draft-issue.draft-issue': {
      let loadingText: string
      let completedText: string
      if (args.issueNumber) {
        loadingText = args.repository
          ? `Drafting update for @${args.repository}/issues/${args.issueNumber}`
          : 'Drafting update'
        completedText = args.repository
          ? `Drafted update for @${args.repository}/issues/${args.issueNumber}`
          : 'Drafted update'
      } else {
        loadingText = args.repository ? `Drafting issue in ${args.repository}` : 'Drafting issue'
        completedText = args.repository ? `Drafted issue in ${args.repository}` : 'Drafted issue'
      }
      return {
        id: 'github-draft-issue.draft-issue',
        loadingText,
        completedText,
        icon: IssueDraftIcon,
        name: 'GitHub Draft Issue Step: Draft Issue',
        activeSkillText: loadingText.toLowerCase(),
      }
    }
    case 'github-draft-issue.existing-issue': {
      const loadingText = args.repository ? `Drafting issue in ${args.repository}` : 'Drafting issue'
      const completedText = args.repository ? `Drafted issue in ${args.repository}` : 'Drafted issue'
      return {
        id: 'github-draft-issue.existing-issue',
        loadingText,
        completedText,
        icon: IssueDraftIcon,
        name: 'GitHub Draft Issue Step: Existing Issue',
        activeSkillText: loadingText.toLowerCase(),
      }
    }
    case 'github-draft-issue.repository-metadata': {
      const loadingText = `Fetching metadata from ${args.repository}`
      const completedText = `Using metadata from ${args.repository}`
      return {
        id: 'github-draft-issue.repository-metadata',
        loadingText,
        completedText,
        icon: IssueDraftIcon,
        name: 'GitHub Draft Issue Step: Repository Metadata',
        activeSkillText: loadingText.toLowerCase(),
      }
    }
    case 'github-draft-issue.get-issues': {
      const loadingText = (args.issueRefs || []).length > 1 ? 'Fetching issues' : 'Fetching issue'
      const completedText = (args.issueRefs || []).length > 1 ? 'Fetched issues' : 'Fetched issue'
      return {
        id: 'github-draft-issue.get-issues',
        loadingText,
        completedText,
        icon: IssueDraftIcon,
        name: 'GitHub Draft Issue Step: Get Issues',
        activeSkillText: loadingText.toLowerCase(),
      }
    }
    case 'github-coding-agent-':
    case 'github-coding-agent': {
      const loadingText = `Creating agent task in ${args.repository}`
      const completedText = `Created agent task in ${args.repository}`
      return {
        id: args.kind,
        loadingText,
        completedText,
        icon: AgentTaskIcon,
        name: 'Copilot Coding Agent',
        activeSkillText: loadingText.toLowerCase(),
      }
    }
  }
}

function FunctionCompleted({maxWidth, functionMetadata}: {maxWidth: number; functionMetadata: FunctionMetadata}) {
  return (
    <>
      {functionMetadata.icon()}
      <Truncate title={functionMetadata.completedText} sx={{maxWidth}}>
        {functionMetadata.completedText}
      </Truncate>
    </>
  )
}

function FunctionLoading({maxWidth, functionMetadata}: {maxWidth: number; functionMetadata: FunctionMetadata}) {
  return (
    <>
      {functionMetadata.icon()}
      <Truncate title={functionMetadata.loadingText} sx={{maxWidth}}>
        {functionMetadata.loadingText}
      </Truncate>
    </>
  )
}

function FunctionError({
  functionCall,
  functionMetadata,
}: {
  functionCall: SkillExecution
  functionMetadata: FunctionMetadata
}) {
  return (
    <div className={styles.errorContainer}>
      <Heading as="h3" className={styles.errorHeading}>
        An error occurred while {functionMetadata.activeSkillText}
      </Heading>
      <p className={styles.errorMessage}>{functionCall.errorMessage}</p>
      <p className={styles.toolDescriptionText}>
        Copilot used the <span className={styles.toolNameHighlight}>{functionMetadata.name}</span> tool
      </p>
    </div>
  )
}

function FunctionTitle({
  functionCall,
  functionMetadata,
  panelWidth,
}: {
  functionCall: SkillExecution
  functionMetadata: FunctionMetadata
  panelWidth: number | undefined
}) {
  const maxWidth = panelWidth ? panelWidth - 130 : copilotLocalStorage.DEFAULT_PANEL_WIDTH
  const innerElement =
    functionCall?.status === 'error' ? (
      <FunctionError functionCall={functionCall} functionMetadata={functionMetadata} />
    ) : functionCall?.status === 'completed' ? (
      <FunctionCompleted maxWidth={maxWidth} functionMetadata={functionMetadata} />
    ) : (
      <FunctionLoading maxWidth={maxWidth} functionMetadata={functionMetadata} />
    )

  return <div className={styles.functionTitleContainer}>{innerElement}</div>
}

function functionRenderer(
  functionCall: SkillExecution,
  functionMetadata: FunctionMetadata,
  manager: CopilotChatManager,
  panelWidth: number | undefined,
  parsedArgs: FunctionArguments,
  useSelectReference: boolean,
) {
  // We may need a better way to indicate that a function call failed due to a confirmation error
  // like a status of confirmationNeeded or some other flag.
  if (functionCall && functionCall.errorMessage !== 'confirmation error') {
    const commonProps = {functionCall, functionMetadata, panelWidth, parsedArgs}

    switch (parsedArgs.kind) {
      case 'bing-search':
        return <BingGroundingFunctionButton chatManager={manager} functionCall={functionCall} skillArgs={parsedArgs} />
      case 'lexical-code-search':
      case 'semantic-code-search':
        return (
          <CodeSearchButton
            chatManager={manager}
            functionCall={functionCall}
            skillArgs={parsedArgs}
            useSelectReference={useSelectReference}
          />
        )
      case 'getalert':
        return (
          <GetAlertButton
            chatManager={manager}
            functionCall={functionCall}
            skillArgs={parsedArgs}
            useSelectReference={useSelectReference}
          />
        )
      case 'getfile':
        return (
          <GetFileButton
            chatManager={manager}
            functionCall={functionCall}
            skillArgs={parsedArgs}
            useSelectReference={useSelectReference}
          />
        )
      case 'getfilechanges':
        return (
          <GetFileChangesButton
            chatManager={manager}
            functionCall={functionCall}
            skillArgs={parsedArgs}
            useSelectReference={useSelectReference}
          />
        )
      case 'getdiscussion':
        return (
          <GetDiscussionButton
            chatManager={manager}
            functionCall={functionCall}
            skillArgs={parsedArgs}
            useSelectReference={useSelectReference}
          />
        )
      case 'get-actions-job-logs':
        return (
          <GetJobLogsButton
            chatManager={manager}
            functionCall={functionCall}
            skillArgs={parsedArgs}
            useSelectReference={useSelectReference}
          />
        )
      case 'planskill':
        return <PlanButton functionCall={functionCall} />
      case 'get-github-data':
        return (
          <GetGitHubDataButton
            chatManager={manager}
            functionCall={functionCall}
            skillArgs={parsedArgs}
            useSelectReference={useSelectReference}
          />
        )
      case 'support-search':
        return <SupportSearchButton chatManager={manager} functionCall={functionCall} skillArgs={parsedArgs} />
      case 'get-figma':
        if (!copilotFeatureFlags.immersiveFigmaIntegration) return <></>
        return <FigmaButton chatManager={manager} functionCall={functionCall} skillArgs={parsedArgs} />
      default: {
        return (
          <div className={`border rounded-2 p-2`}>
            <FunctionTitle {...commonProps} />
          </div>
        )
      }
    }
  } else {
    return null
  }
}

export function useFunctionMetadata(functionCall: SkillExecution): {
  functionMetadata: FunctionMetadata | undefined
  functionRenderer: typeof functionRenderer | undefined
  parsedArgs: FunctionArguments | undefined
} {
  return useMemo(() => {
    if (!functionCall.arguments || !SUPPORTED_FUNCTIONS.includes(functionCall.slug)) {
      return {
        functionMetadata: undefined,
        functionRenderer: undefined,
        parsedArgs: undefined,
      }
    }
    const parsedArgs = {
      ...JSON.parse(functionCall.arguments),
      kind: functionCall.slug,
    } as FunctionArguments
    const functionMetadata = functionMetadataCollection(parsedArgs)

    return {
      functionMetadata,
      functionRenderer,
      parsedArgs,
    }
  }, [functionCall])
}

export function messageFromFunctionCall(functionCall?: SkillExecution) {
  if (!functionCall) return DEFAULT_RESPONDING_MESSAGE
  const functionMetadata = metadataFromFunctionCall(functionCall)

  return functionCall.statusMessage ?? functionMetadata?.activeSkillText ?? DEFAULT_RESPONDING_MESSAGE
}

export function metadataFromFunctionCall(functionCall: SkillExecution) {
  if (!functionCall) return undefined

  const parsedArgs = {
    ...JSON.parse(functionCall?.arguments || '{}'),
    kind: functionCall.slug,
  } as FunctionArguments

  return functionMetadataCollection(parsedArgs)
}

try{ CodeSearchIcon.displayName ||= 'CodeSearchIcon' } catch {}
try{ GetFileIcon.displayName ||= 'GetFileIcon' } catch {}
try{ ReleaseIcon.displayName ||= 'ReleaseIcon' } catch {}
try{ JobIcon.displayName ||= 'JobIcon' } catch {}
try{ SearchBookIcon.displayName ||= 'SearchBookIcon' } catch {}
try{ DiscussionIcon.displayName ||= 'DiscussionIcon' } catch {}
try{ PlanIcon.displayName ||= 'PlanIcon' } catch {}
try{ IssueDraftIcon.displayName ||= 'IssueDraftIcon' } catch {}
try{ AgentTaskIcon.displayName ||= 'AgentTaskIcon' } catch {}
try{ FunctionCompleted.displayName ||= 'FunctionCompleted' } catch {}
try{ FunctionLoading.displayName ||= 'FunctionLoading' } catch {}
try{ FunctionError.displayName ||= 'FunctionError' } catch {}
try{ FunctionTitle.displayName ||= 'FunctionTitle' } catch {}